import { useEffect, useState } from "react";
import { isLocalHostUrl } from "@greg-chick/shared";
import { countPendingPull, adminApi } from "../lib/contentApi";

/**
 * **"올렸는데 왜 안 보이지"를 없앤다.**
 *
 * ## 왜 필요한가
 * 관리자로 올린 그림·소리는 **엣지의 R2 에만** 들어간다. 개발용 엣지(내 PC)에서 올렸다면
 * 저장소에는 없다 — **배포하면 그 그림들은 처음부터 없는 상태로 시작한다.**
 * 그런데 관리자 화면에서는 자기 R2 를 읽으니 **멀쩡히 보인다.** 물어볼 이유조차 없다.
 *
 * 실제로 그렇게 56개(16.8MB)가 배포에서 빠져 있었다(docs/build/20 A1).
 * 회수 도구(`pnpm art:pull`)는 있지만 운영자는 그런 게 있는 줄 모른다 — 화면에서 말해 준다.
 *
 * ## 왜 여기서 고쳐 주지 않나
 * 브라우저는 저장소에 파일을 못 쓴다. 그래서 **알려 주는 것까지**가 이 화면의 몫이다.
 * "모르는 채로 배포되는 것"과 "알고 개발자에게 말하는 것"은 완전히 다르다.
 */
export function PendingDeploy() {
  const [pending, setPending] = useState<string[]>([]);
  const [unknown, setUnknown] = useState(true);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      const r = await countPendingPull();
      if (!alive) return;
      setPending(r.pending);
      setUnknown(r.unknown);
    };
    void check();
    // 올린 직후에 저절로 반영되도록(운영자가 새로고침을 몰라도 된다)
    const id = setInterval(() => void check(), 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // 모르는 상태에서 "괜찮다"고 하지 않는다 — 그냥 아무 말도 안 한다(ContentReach 가 원인을 띄운다)
  if (unknown || pending.length === 0) return null;

  const local = isLocalHostUrl(adminApi());
  return (
    <div className="pending" role="status">
      <b className="pending__title">
        📦 여기서 올린 그림 {pending.length}개가 <u>아직 배포에 안 들어갑니다</u>
      </b>
      <span className="pending__body">
        지금은 <b>이 컴퓨터에만</b> 저장돼 있어요. 화면에는 잘 보이지만, 실제 홈페이지에는 안 올라갑니다.
        {local ? " 개발자에게 아래를 실행해 달라고 하세요." : ""}
      </span>
      {local ? <code className="pending__cmd">pnpm art:pull</code> : null}
      <details className="pending__more">
        <summary>어떤 것들인가요? ({pending.length}개)</summary>
        <ul className="pending__list">
          {pending.slice(0, 40).map((k) => (
            <li key={k}>{k}</li>
          ))}
          {pending.length > 40 ? <li>… 그 밖에 {pending.length - 40}개</li> : null}
        </ul>
      </details>
    </div>
  );
}
